export class ApiV2Error extends Error {
    constructor({ status, code, message, details = {}, requestId = null }) {
        super(message);
        this.name = 'ApiV2Error';
        this.status = status;
        this.code = code;
        this.details = details;
        this.requestId = requestId;
    }
}

function buildApiV2Url(apiRoot, path, query = null) {
    if (typeof path !== 'string' || !path.startsWith('/')) {
        throw new TypeError('API v2 paths must start with /.');
    }
    if (path === '/v2' || path.startsWith('/v2/')) {
        throw new TypeError('API v2 paths must not include the /v2 prefix.');
    }

    const normalizedRoot = String(apiRoot || '').replace(/\/+$/, '');
    if (!normalizedRoot) {
        throw new TypeError('An API root is required.');
    }

    const url = new URL(`${normalizedRoot}/v2${path}`);
    if (query) {
        Object.entries(query).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') return;
            if (Array.isArray(value)) {
                value.forEach((entry) => url.searchParams.append(key, entry));
                return;
            }
            url.searchParams.set(key, value);
        });
    }
    return url.toString();
}

function requestSignal(signal, timeoutMs) {
    if (timeoutMs === undefined || timeoutMs === null) {
        return { signal, timedOut: () => false, cleanup() {} };
    }
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1) {
        throw new TypeError('timeoutMs must be a positive integer.');
    }

    const controller = new AbortController();
    let didTimeOut = false;
    const abortFromCaller = () => controller.abort(signal?.reason);
    if (signal?.aborted) abortFromCaller();
    else signal?.addEventListener('abort', abortFromCaller, { once: true });
    const timer = setTimeout(() => {
        didTimeOut = true;
        controller.abort();
    }, timeoutMs);

    return {
        signal: controller.signal,
        timedOut: () => didTimeOut,
        cleanup() {
            clearTimeout(timer);
            signal?.removeEventListener('abort', abortFromCaller);
        },
    };
}

async function readResponseBody(response, requireValidJson = false) {
    if (response.status === 204 || response.status === 205) return null;

    const text = await response.text();
    if (!text.trim()) {
        if (!requireValidJson) return null;
        throw new ApiV2Error({
            status: response.status,
            code: 'INVALID_API_RESPONSE',
            message: 'The server returned an invalid API response.',
        });
    }

    try {
        return JSON.parse(text);
    } catch {
        if (!requireValidJson) return null;
        throw new ApiV2Error({
            status: response.status,
            code: 'INVALID_API_RESPONSE',
            message: 'The server returned an invalid API response.',
        });
    }
}

export function createApiV2Client({
    apiRoot,
    fetchImpl = globalThis.fetch,
} = {}) {
    if (typeof fetchImpl !== 'function') {
        throw new TypeError('A fetch implementation is required.');
    }

    return {
        async request(
            path,
            {
                method = 'GET',
                query = null,
                body,
                ifMatch,
                idempotencyKey,
                signal,
                timeoutMs,
                responseType = 'json',
            } = {}
        ) {
            if (!['json', 'blob'].includes(responseType)) {
                throw new TypeError('responseType must be json or blob.');
            }
            const headers = new Headers({
                Accept:
                    responseType === 'blob'
                        ? 'application/octet-stream'
                        : 'application/json',
            });
            const isFormData =
                typeof FormData !== 'undefined' && body instanceof FormData;
            if (body !== undefined && !isFormData) {
                headers.set('Content-Type', 'application/json');
            }
            if (ifMatch !== undefined && ifMatch !== null) {
                headers.set('If-Match', String(ifMatch));
            }
            if (idempotencyKey) {
                headers.set('Idempotency-Key', idempotencyKey);
            }

            const deadline = requestSignal(signal, timeoutMs);
            try {
                const response = await fetchImpl(
                    buildApiV2Url(apiRoot, path, query),
                    {
                        method,
                        credentials: 'include',
                        headers,
                        signal: deadline.signal,
                        ...(body === undefined
                            ? {}
                            : {
                                  body: isFormData
                                      ? body
                                      : JSON.stringify(body),
                              }),
                    }
                );
                if (response.ok && responseType === 'blob') {
                    return response.blob();
                }
                const responseBody = await readResponseBody(
                    response,
                    response.ok
                );

                if (response.ok) return responseBody;

                const apiError = responseBody?.error;
                throw new ApiV2Error({
                    status: response.status,
                    code: apiError?.code || 'API_REQUEST_FAILED',
                    message:
                        apiError?.message ||
                        `API request failed with status ${response.status}.`,
                    details: apiError?.details || {},
                    requestId:
                        apiError?.requestId ||
                        response.headers.get('X-Request-ID') ||
                        null,
                });
            } catch (error) {
                if (deadline.timedOut()) {
                    throw new ApiV2Error({
                        status: 0,
                        code: 'API_REQUEST_TIMEOUT',
                        message: 'The request took too long. Try again.',
                    });
                }
                throw error;
            } finally {
                deadline.cleanup();
            }
        },
    };
}

export { buildApiV2Url };
